import { FC, useState, useEffect, useCallback } from 'react';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import {
    Box,
    Chip,
    Divider,
    Fade,
    Grid,
    IconButton,
    LinearProgress,
    ListItemIcon,
    ListItemText,
    Menu,
    MenuItem,
    Paper,
    Stack,
    Tooltip,
    Typography,
    styled,
} from '@mui/material';
import { SCUICustomDataGrid, SCUIGeneralDrawer, SCUISnackbar } from '../../atomic/molecules';
import { SCUIButton } from '../../atomic/atoms';
import { SCUIContent } from '../../atomic/organisms';
import { useTranslation } from 'react-i18next';
import { GridSortModel } from '@mui/x-data-grid';
import { ExtendedGridColDef } from '@/types/ExtendedGridColDef';
import { useHistory } from 'react-router-dom';
import { CampaignManagementQuery } from '../../services/query';
import {
    Add,
    AccessTime,
    AutoAwesome,
    Circle,
    MoreVert,
    Search,
    Today,
    PauseOutlined,
    EditOutlined,
    DoDisturbOnOutlined,
} from '@mui/icons-material';
import PopupState, { bindTrigger, bindMenu } from 'material-ui-popup-state';
import { CampaignServiceComponent } from './Campaign.helper';
import { SCUICampaignSearch } from './components/SCUICampaignSearch/SCUICampaignSearch';
import { ConfirmationDialogWithTrigger } from './components/SCUIConfirmationDialog/SCUIConfirmationDialogWithTrigger';
import { SCUIAISuggestionDialog } from './components/SCUIAISuggestionDialog/SCUIAISuggestionDialog';
import {
    CampaignSortModel,
    CampaignStatus,
    CampaignStatuses,
    RulePlatformValues,
} from '../../types/enums/campaign-enums';
import './CampaignManagement.scss';
import moment from 'moment';
import Helper from '../../utils/helper';
import { CampaignManagementCommand } from '../../services/command';
import { enumSnackbarStatusType } from '../../types/enums';
import { hideSnackbar, showSnackbar } from '../../stores/redux/actions';
import { useDispatch, useSelector } from 'react-redux';

export const CampaignManagement: FC<any> = (props: any) => {
    const dispatch = useDispatch();

    const { t } = useTranslation();
    const history = useHistory();
    const regionStore = useSelector((state: any) => state.region);

    const countryId = useSelector((state: any) => state.lastRegionReducer.setLastRegion);

    const snacbarStore = useSelector((state: any) => state.snackbar);

    const service = CampaignServiceComponent({ ...props });
    const { initialState, loading, showLoading, hideLoading } = service;

    const columns: ExtendedGridColDef[] = [
        {
            field: 'aiSuggestion',
            headerName: '',
            width: 50,
            filterable: false,
            sortable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <Tooltip title="AI Suggestion">
                    <IconButton
                        size="small"
                        onClick={() => {
                            setAiCampaignId(params.row.id);
                            setAiDialogOpen(true);
                        }}
                        sx={{ color: 'primary.main' }}
                    >
                        <AutoAwesome sx={{ fontSize: 18 }} />
                    </IconButton>
                </Tooltip>
            ),
        },
        { field: 'id', headerName: t('campaignManagement.list.column.id'), width: 100, hideable: false },
        {
            field: 'status',
            headerName: t('campaignManagement.list.column.status'),
            flex: 0.4,
            renderCell: (cellInfo) => (
                <Tooltip
                    title={t(CampaignStatuses.find((x: any) => x.id == cellInfo.row.status)?.name ?? '')}
                    style={{ fontSize: '16px!important' }}
                >
                    <Circle
                        style={{
                            height: 16,
                            width: 16,
                            fontSize: '16px',
                            color: CampaignStatuses.find((x: any) => x.id == cellInfo.row.status)?.color,
                        }}
                    />
                </Tooltip>
            ),
            filterable: false,
            disableReorder: false,
            sortable: false,
            hide: false,
            editable: false,
            resizable: false,
            groupable: false,
            disableColumnMenu: true,
        },
        { field: 'name', headerName: t('campaignManagement.list.column.campaignName'), flex: 1 },
        {
            field: 'applicationScopeType',
            headerName: t('campaignManagement.list.column.applicationScopeType'),
            flex: 1,
            disableReorder: false,
            sortable: false,
        },
        {
            field: 'beginDate',
            headerName: t('campaignManagement.list.column.beginDate'),
            flex: 1,
            renderCell: (cellInfo) => (
                <Chip
                    label={new Intl.DateTimeFormat(navigator.language).format(new Date(cellInfo.row.beginDate))}
                    icon={<Today />}
                />
            ),
        },
        {
            field: 'endDate',
            headerName: t('campaignManagement.list.column.endDate'),
            flex: 1,
            renderCell: (cellInfo) => (
                <Chip
                    label={new Intl.DateTimeFormat(navigator.language).format(new Date(cellInfo.row.endDate))}
                    icon={<Today />}
                />
            ),
        },
        {
            field: 'startTime',
            headerName: t('campaignManagement.list.column.startTime'),
            flex: 1,
            renderCell: (cellInfo) => (
                <Chip
                    label={new Intl.DateTimeFormat(navigator.language, {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    }).format(new Date(cellInfo.row.beginDate))}
                    icon={<AccessTime />}
                />
            ),
            hide: true,
        },
        {
            field: 'endTime',
            headerName: t('campaignManagement.list.column.endTime'),
            flex: 1,
            renderCell: (cellInfo) => (
                <Chip
                    label={new Intl.DateTimeFormat(navigator.language, {
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                    }).format(new Date(cellInfo.row.endDate))}
                    icon={<AccessTime />}
                />
            ),
            hide: true,
        },
        { field: 'assignee', headerName: t('campaignManagement.list.column.assignee'), flex: 1 },
        {
            field: 'processOrder',
            headerName: t('campaignManagement.list.column.priority'),
            renderCell: (cellInfo) => (cellInfo.row.status == CampaignStatus.Passive ? '' : cellInfo.row.processOrder),
            flex: 1,
            hide: true,
        },
        {
            field: 'platform',
            headerName: t('campaignManagement.list.column.platform'),
            flex: 1,
            renderCell: (cellInfo) => (
                <Tooltip
                    title={RulePlatformValues.filter((x: any) => cellInfo.row.platforms.includes(x.id))
                        .map((y: any) => y.name)
                        .join(', ')}
                >
                    <Typography>
                        {RulePlatformValues.filter((x: any) => cellInfo.row.platforms.includes(x.id))
                            .map((y: any) => y.name)
                            .join(', ')}
                    </Typography>
                </Tooltip>
            ),
            hide: true,
        },
        { field: 'tag', headerName: t('campaignManagement.list.column.tag'), flex: 1, hide: true },
        {
            field: t('campaignManagement.list.column.actions'),
            headerName: '',
            align: 'right',
            width: 50,
            filterable: false,
            disableReorder: false,
            sortable: false,
            hide: false,
            editable: false,
            resizable: false,
            groupable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const handleEditClick = () => {
                    Helper.openInNewTab(`/campaign/campaignmanagement/save/${params.row.id}`);
                };

                return (
                    <PopupState variant="popover">
                        {(popupState: any) => (
                            <>
                                <IconButton {...bindTrigger(popupState)}>
                                    <MoreVert className="svg_icons" />
                                </IconButton>
                                <Menu {...bindMenu(popupState)}>
                                    <MenuItem onClick={handleEditClick}>
                                        <ListItemIcon>
                                            <EditOutlined fontSize="small" />
                                        </ListItemIcon>
                                        <ListItemText>{t('campaignManagement.list.column.action.edit')}</ListItemText>
                                    </MenuItem>
                                    <Divider style={{ width: '100%', margin: '0' }} />
                                    <ConfirmationDialogWithTrigger
                                        triggerComponent={
                                            <MenuItem
                                                disabled={
                                                    params.row.status == CampaignStatus.Paused ||
                                                    params.row.status == CampaignStatus.Passive
                                                }
                                            >
                                                <ListItemIcon>
                                                    <PauseOutlined fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {t('campaignManagement.save.step.summary.action.pausedButton')}
                                                </ListItemText>
                                            </MenuItem>
                                        }
                                        title={t('campaignManagement.save.step.summary.dialog.title')}
                                        description={parse(
                                            DOMPurify.sanitize(
                                                t('campaignManagement.save.step.summary.dialog.pausedMessage')
                                            )
                                        )}
                                        onConfirm={() => updateStatus(params.row.id, CampaignStatus.Paused)}
                                    />
                                    <ConfirmationDialogWithTrigger
                                        triggerComponent={
                                            <MenuItem disabled={params.row.status == CampaignStatus.Passive}>
                                                <ListItemIcon>
                                                    <DoDisturbOnOutlined fontSize="small" />
                                                </ListItemIcon>
                                                <ListItemText>
                                                    {t('campaignManagement.save.step.summary.action.passiveButton')}
                                                </ListItemText>
                                            </MenuItem>
                                        }
                                        title={t('campaignManagement.save.step.summary.dialog.title')}
                                        description={parse(
                                            DOMPurify.sanitize(
                                                t('campaignManagement.save.step.summary.dialog.passiveMessage')
                                            )
                                        )}
                                        onConfirm={() => updateStatus(params.row.id, CampaignStatus.Passive)}
                                    />
                                </Menu>
                            </>
                        )}
                    </PopupState>
                );
            },
        },
    ];

    const [rows, setRows] = useState<any>([]);

    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [sortModel, setSortModel] = useState<GridSortModel>([
        {
            field: 'Id',
            sort: 'desc',
        },
    ]);

    const [chipList, setFilterChipList] = useState<any>([]);

    const [filterInput, setFilterInput] = useState<any>(initialState.campaignFilters);

    const [openFilter, setOpenFilter] = useState<boolean>(false);
    const [applyFilter, setApplyFilter] = useState<boolean>(false);

    const [aiDialogOpen, setAiDialogOpen] = useState(false);
    const [aiCampaignId, setAiCampaignId] = useState<number | null>(null);

    useEffect(() => {
        search();
    }, [regionStore, page, pageSize, sortModel, countryId]);

    useEffect(() => {
        if (applyFilter) {
            search().then(() => {
                setApplyFilter(false);
            });
        }
    }, [applyFilter]);

    useEffect(() => {
        renderChip();
    }, [rows]);

    const getCampaignList = async (
        name?: any,
        applicationScopeType?: any,
        beginDate?: any,
        endDate?: any,
        startTime?: any,
        endTime?: any,
        status?: any,
        region?: any,
        assignee?: any,
        platforms?: any,
        tag?: any,
        isMatrix?: any,
        skipCount: number = 0,
        maxResultCount: number = 10,
        sortPropertyName: string = 'Id',
        sortValue: number = 1
    ) => {
        showLoading();

        if (beginDate != null) {
            beginDate += startTime?.length > 0 ? `T${startTime}` : '';
        }

        if (endDate != null) {
            endDate += endTime?.length > 0 ? `T${endTime}` : '';
        }

        const campaignList = await CampaignManagementQuery.GetCampaignList({
            name,
            applicationScopeType,
            beginDate,
            endDate,
            status,
            region,
            assignee,
            platforms,
            tag,
            isMatrix,
            skipCount,
            maxResultCount,
            sortPropertyName,
            sortValue,
        });

        setRows(campaignList?.data?.items || []);
        setTotalCount(campaignList?.data?.totalCount ?? 0);
        hideLoading();

        return campaignList;
    };

    const search = async () => {
        if (!loading) {
            let sortPropertyName = sortModel[0]?.field ?? 'Id';
            let sortValue = sortModel[0].sort == 'asc' ? CampaignSortModel.Ascending : CampaignSortModel.Descending;
            let campaingName = filterInput.campaignName?.id ?? null;
            let applicationScopeTypeId = filterInput.applicationScopeType?.id ?? null;
            let beginDate = filterInput.beginDate?.id ?? null;
            let endDate = filterInput.endDate?.id ?? null;
            let startTime = filterInput.startTime?.id ?? null;
            let endTime = filterInput.endTime?.id ?? null;
            let assignee = filterInput.assignee?.name ?? null;
            let statusId = filterInput.status?.id ?? null;
            let regionId = Number(regionStore?.region);
            let platformIds = filterInput.platforms && filterInput.platforms.map((x: any) => x.id);
            let tag = filterInput.tag?.id ?? null;
            let isMatrix = filterInput.isMatrix?.id ?? null;

            await getCampaignList(
                campaingName,
                applicationScopeTypeId,
                beginDate,
                endDate,
                startTime,
                endTime,
                statusId,
                regionId,
                assignee,
                platformIds,
                tag,
                isMatrix,
                page,
                pageSize,
                sortPropertyName,
                sortValue
            );
        }

        setOpenFilter(false);
    };

    const renderChip = () => {
        let chipList: any[] = [];

        if (filterInput.campaignName?.id != undefined) {
            chipList.push({
                name: t('campaignManagement.list.chip.campaignName'),
                value: filterInput.campaignName.name,
                type: 'campaignName',
                id: filterInput.campaignName.id,
            });
        }

        if (filterInput.applicationScopeType?.name) {
            chipList.push({
                name: t('campaignManagement.list.chip.applicationScopeType'),
                value: t(filterInput.applicationScopeType.name),
                type: 'applicationScopeType',
                id: filterInput.applicationScopeType.id,
            });
        }

        if (filterInput.beginDate?.id != undefined || filterInput.endDate?.id != undefined) {
            let dateRange = `${moment(filterInput.beginDate?.id).format('DD.MM.YYYY') ?? ''} - ${
                moment(filterInput.endDate?.id).format('DD.MM.YYYY') ?? ''
            }`;
            let dateObject: any = {
                beginDate: filterInput.beginDate?.id,
                endDate: filterInput.endDate?.id,
            };

            chipList.push({
                name: t('campaignManagement.list.chip.dateRange'),
                value: dateRange,
                type: 'dateRange',
                id: dateRange,
                object: dateObject,
            });
        }

        if (
            (filterInput.beginDate?.id != undefined || filterInput.endDate?.id != undefined) &&
            (filterInput.startTime?.id != undefined || filterInput.endTime?.id != undefined)
        ) {
            let timeRange = `${filterInput.startTime?.id ?? ''} - ${filterInput.endTime?.id ?? ''}`;
            let timeObject: any = {
                startTime: filterInput.startTime?.id,
                endTime: filterInput.endTime?.id,
            };

            chipList.push({
                name: t('campaignManagement.list.chip.timeRange'),
                value: timeRange,
                type: 'timeRange',
                id: timeRange,
                object: timeObject,
            });
        }

        if (filterInput.assignee?.name) {
            chipList.push({
                name: t('campaignManagement.list.chip.assignee'),
                value: filterInput.assignee.name,
                type: 'assignee',
                id: filterInput.assignee.id,
            });
        }

        if (filterInput.tag?.name) {
            chipList.push({
                name: t('campaignManagement.list.chip.tag'),
                value: filterInput.tag.name,
                type: 'tag',
                id: filterInput.tag.id,
            });
        }

        if (filterInput.platforms?.length > 0) {
            if (filterInput.platforms) {
                filterInput.platforms.map((item: any) => {
                    chipList.push({
                        name: t('campaignManagement.list.chip.platform'),
                        value: item.name,
                        type: 'platforms',
                        id: item.id,
                    });
                });
            }
        }

        if (filterInput.status?.name) {
            chipList.push({
                name: t('campaignManagement.list.chip.status'),
                value: t(filterInput.status.name),
                type: 'status',
                id: filterInput.status.id,
            });
        }

        setFilterChipList(chipList);
        return chipList;
    };

    const deleteFilter = (item: any) => {
        let _filterInput = { ...filterInput };
        if (_filterInput != null) {
            switch (item.type) {
                case 'dateRange':
                    _filterInput['beginDate'] = null;
                    _filterInput['endDate'] = null;
                    break;
                case 'timeRange':
                    _filterInput['startTime'] = null;
                    _filterInput['endTime'] = null;
                    break;
                case 'status':
                    _filterInput['status'] = null;
                    break;
                default:
                    _filterInput[item.type] = initialState.campaignFilters[item.type];
                    break;
            }
        }

        setFilterInput(_filterInput);
        setApplyFilter(true);
        return _filterInput;
    };

    const updateStatus = async (id: number, status: number) => {
        const response = await CampaignManagementCommand.UpdateStatus({
            id: id,
            status: status,
            culture: Helper.getLang(),
        });

        if (response == undefined || response == null || !response.isSuccess) {
            dispatch(
                showSnackbar({
                    message:
                        response.message ??
                        t('campaignManagement.save.step.message.anErrorOccurredWhileUpdatingStatus'),
                    status: enumSnackbarStatusType.danger,
                })
            );
        } else {
            setApplyFilter(true);

            dispatch(
                showSnackbar({
                    message: t('campaignManagement.save.step.message.statusUpdated'),
                    status: enumSnackbarStatusType.success,
                })
            );
        }
    };

    const Item = styled(Paper)(({ theme }) => ({
        ...theme.typography.body2,
        backgroundColor: 'transparent',
        padding: theme.spacing(1),
        textAlign: 'center',
        color: theme.palette.text.secondary,
    }));

    const DataList = () => {
        const handleSortChange = useCallback((model: GridSortModel) => {
            if (JSON.stringify(model) !== JSON.stringify(sortModel)) {
                setSortModel(model);
            }
        }, []);

        return (
            <Grid container direction="row" spacing={2}>
                <Grid size={{ xs: 8, md: 8, lg: 8, xl: 8 }} style={{ display: 'flex', alignItems: 'center' }}>
                    <h1 className="headtxt">{t('campaignManagement.dashboard.header')}</h1>
                </Grid>

                <Grid size={{ xs: 4, md: 4, lg: 4, xl: 4 }}>
                    <Stack direction="row" style={{ justifyContent: 'end' }}>
                        <Item>
                            <SCUIButton
                                showStartIcon={true}
                                startIcon={<Search style={{ height: '32', color: '#0d6efd', marginLeft: 0 }} />}
                                text={''}
                                type="secondary"
                                sizeType="icon"
                                size={'s'}
                                buttonType="button"
                                onClick={() => setOpenFilter(true)}
                            />
                        </Item>
                        <Item>
                            <SCUIButton
                                showStartIcon={true}
                                startIcon={<Add style={{ color: 'white' }} />}
                                text={t('campaignManagement.list.createButton')}
                                type="primary"
                                sizeType="fixed"
                                size={'s'}
                                buttonType="button"
                                onClick={() => {
                                    history.push('/campaign/campaignmanagement/save');
                                }}
                            />
                        </Item>
                    </Stack>
                </Grid>

                <Grid
                    size={{ xs: 12, md: 12, lg: 12, xl: 12 }}
                    style={{ display: chipList.length > 0 ? 'block' : 'none' }}
                >
                    <p style={{ marginTop: 5, marginBottom: 10 }}>{t('campaignManagement.list.appliedFilters')}</p>
                </Grid>

                <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                    <Stack direction="row" spacing={1} style={{ maxWidth: '100%', overflowY: 'auto' }}>
                        {chipList &&
                            chipList.map((item: any) => {
                                let labelText = `${item.name} : ${item.value}`;
                                return (
                                    <Chip
                                        key={item.id}
                                        label={`${labelText}`}
                                        onDelete={() => {
                                            deleteFilter(item);
                                        }}
                                    />
                                );
                            })}
                    </Stack>
                </Grid>

                <Grid size={{ xs: 12, md: 12, lg: 12, xl: 12 }}>
                    <SCUICustomDataGrid
                        randomId
                        loading={loading}
                        columns={columns}
                        rows={rows}
                        rowCount={totalCount}
                        pagination
                        isHeadersCached={true}
                        page={page}
                        pageSize={pageSize}
                        paginationModeChange={true}
                        onPageChange={(newPage: number) => {
                            setPage(newPage);
                        }}
                        onPageSizeChange={(newPageSize: number) => {
                            setPageSize(newPageSize);
                            setPage(0);
                        }}
                        sortingMode="server"
                        onSortModelChange={(model: GridSortModel) => handleSortChange(model)}
                    />
                </Grid>
            </Grid>
        );
    };

    return (
        <>
            <Box className="campaignLoading">
                <Fade in={loading} unmountOnExit>
                    <LinearProgress />
                </Fade>
            </Box>
            <SCUIContent>
                <DataList />
            </SCUIContent>
            <SCUISnackbar
                show={snacbarStore.open}
                messageText={snacbarStore.message}
                status={snacbarStore.status}
                autoHideDuration={snacbarStore.duration}
                onClose={() => {
                    dispatch(hideSnackbar());
                }}
            />
            <SCUIGeneralDrawer
                name="SearchFilterDrawer"
                open={openFilter}
                width={'450px'}
                onClose={() => {
                    setOpenFilter(false);
                }}
            >
                <SCUICampaignSearch
                    {...props}
                    campaignService={service}
                    filterInput={filterInput}
                    setFilterInput={setFilterInput}
                    setData={setRows}
                    search={search}
                    onClose={() => setOpenFilter(false)}
                />
            </SCUIGeneralDrawer>
            <SCUIAISuggestionDialog
                open={aiDialogOpen}
                campaignId={aiCampaignId}
                onClose={() => {
                    setAiDialogOpen(false);
                    setAiCampaignId(null);
                }}
            />
        </>
    );
};
